import React, { useEffect, useMemo, useState } from 'react';
import { Youtube, Instagram, PlayCircle, ExternalLink, X } from 'lucide-react';
import socialFeedService from '../../services/socialFeedService';
import analyticsService from '../../services/analyticsService';

// Fallbacks only — the live links come from Platform Integrations (youtubeChannelId,
// instagramUsername) via the backend feed endpoints. These cover the gap before that's saved.
const FALLBACK_YOUTUBE_CHANNEL_URL = 'https://www.youtube.com/channel/UCD6QavOPOQOIDTfXn8J_8WQ';
const FALLBACK_INSTAGRAM_PROFILE_URL = 'https://www.instagram.com/zee_sails/';

const VIEW_TABS = [
  { key: 'all', label: 'All' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'instagram', label: 'Instagram' },
];

/** Pulls recent YouTube uploads + Instagram posts from Platform Integrations and shows them on the storefront. */
const SocialMediaFeed = () => {
  const [videos, setVideos] = useState([]);
  const [posts, setPosts] = useState([]);
  const [youtubeChannelUrl, setYoutubeChannelUrl] = useState(null);
  const [instagramProfileUrl, setInstagramProfileUrl] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [viewMode, setViewMode] = useState('all'); // 'all' | 'youtube' | 'instagram' — mixed-by-date vs single platform
  const [playingVideoId, setPlayingVideoId] = useState(null); // plays inline instead of redirecting to youtube.com

  useEffect(() => {
    let cancelled = false;

    Promise.all([socialFeedService.getYoutubeFeed(), socialFeedService.getInstagramFeed()]).then(
      ([youtube, instagram]) => {
        if (cancelled) return;
        setVideos(youtube.videos);
        setPosts(instagram.posts);
        setYoutubeChannelUrl(youtube.channelUrl);
        setInstagramProfileUrl(instagram.profileUrl);
        setLoaded(true);
      }
    );

    return () => {
      cancelled = true;
    };
  }, []);

  // Merge both platforms into one chronological feed (newest first) for the "All" tab.
  const combinedItems = useMemo(() => {
    const videoItems = videos.map((v) => ({ kind: 'video', date: v.publishedAt, data: v }));
    const postItems = posts.map((p) => ({ kind: 'post', date: p.timestamp, data: p }));
    return [...videoItems, ...postItems].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [videos, posts]);

  useEffect(() => {
    if (!playingVideoId) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setPlayingVideoId(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [playingVideoId]);

  if (!loaded) {
    return null;
  }

  const youtubeUrl = youtubeChannelUrl || FALLBACK_YOUTUBE_CHANNEL_URL;
  const instagramUrl = instagramProfileUrl || FALLBACK_INSTAGRAM_PROFILE_URL;

  const videoTile = (video) => (
    <button
      key={`v-${video.id}`}
      type="button"
      onClick={() => {
        analyticsService.trackSocialClick('youtube', video.id, video.title);
        setPlayingVideoId(video.id);
      }}
      style={{
        position: 'relative',
        display: 'block',
        borderRadius: '12px',
        overflow: 'hidden',
        textDecoration: 'none',
        background: 'rgba(255,255,255,0.05)',
        boxShadow: '0 8px 20px rgba(0,0,0,0.4)',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
      }}
    >
      <div style={{ position: 'relative', paddingTop: '56.25%', background: '#111' }}>
        {video.thumbnail && (
          <img
            src={video.thumbnail}
            alt={video.title}
            loading="lazy"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        <PlayCircle
          size={40}
          color="white"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.6))',
          }}
        />
        <Youtube
          size={16}
          color="#ff0000"
          style={{ position: 'absolute', top: '8px', right: '8px', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.8))' }}
        />
      </div>
      <p
        style={{
          color: 'white',
          fontSize: '13px',
          padding: '10px 12px',
          margin: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {video.title}
      </p>
    </button>
  );

  const postTile = (post) => (
    <a
      key={`p-${post.id}`}
      href={post.permalink}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => analyticsService.trackSocialClick('instagram', post.id, post.caption ? post.caption.slice(0, 80) : 'Instagram post')}
      style={{
        display: 'block',
        position: 'relative',
        paddingTop: '100%',
        borderRadius: '12px',
        overflow: 'hidden',
        background: '#111',
      }}
    >
      {post.thumbnailUrl && (
        <img
          src={post.thumbnailUrl}
          alt={post.caption ? post.caption.slice(0, 60) : 'Instagram post'}
          loading="lazy"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      {post.mediaType === 'VIDEO' && (
        <PlayCircle
          size={28}
          color="white"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.6))',
          }}
        />
      )}
      <Instagram
        size={14}
        color="#e1306c"
        style={{ position: 'absolute', top: '8px', right: '8px', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.8))' }}
      />
    </a>
  );

  return (
    <section
      className="zee-feed-section"
      style={{
        position: 'relative',
        zIndex: 50,
        maxWidth: '1200px',
        margin: '0 auto',
        boxSizing: 'border-box',
      }}
    >
      <h2
        style={{
          fontSize: 'clamp(1.5rem, 5vw, 2rem)',
          fontWeight: 'bold',
          marginBottom: '32px',
          color: 'white',
          textAlign: 'center',
          textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
        }}
      >
        Zee Social
      </h2>

      {/* Always-visible cross-promo links — work regardless of whether the live feed below has data. */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          marginBottom: '24px',
        }}
      >
        <a
          href={youtubeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="zee-feed-cta-btn"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            borderRadius: '999px',
            background: '#ff0000',
            color: 'white',
            textDecoration: 'none',
            fontWeight: 600,
          }}
        >
          <Youtube size={18} />
          Subscribe on YouTube
          <ExternalLink size={14} />
        </a>
        <a
          href={instagramUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="zee-feed-cta-btn"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            borderRadius: '999px',
            background: 'linear-gradient(135deg, #833ab4, #fd1d1d, #fcb045)',
            color: 'white',
            textDecoration: 'none',
            fontWeight: 600,
          }}
        >
          <Instagram size={18} />
          Follow on Instagram
          <ExternalLink size={14} />
        </a>
      </div>

      {/* Mixed-by-date vs single-platform toggle */}
      {videos.length > 0 && posts.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '32px' }}>
          {VIEW_TABS.map((tab) => {
            const isActive = viewMode === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setViewMode(tab.key)}
                style={{
                  padding: '8px 18px',
                  borderRadius: '999px',
                  border: isActive ? '2px solid #ec4899' : '1px solid rgba(255,255,255,0.25)',
                  background: isActive ? 'rgba(236,72,153,0.25)' : 'rgba(255,255,255,0.08)',
                  color: 'white',
                  fontWeight: isActive ? 700 : 500,
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      )}

      {viewMode === 'all' && combinedItems.length > 0 && (
        <div className="zee-feed-video-grid" style={{ display: 'grid' }}>
          {combinedItems.map((item) => (item.kind === 'video' ? videoTile(item.data) : postTile(item.data)))}
        </div>
      )}

      {viewMode === 'youtube' && videos.length > 0 && (
        <div className="zee-feed-video-grid" style={{ display: 'grid' }}>
          {videos.map(videoTile)}
        </div>
      )}

      {viewMode === 'instagram' && posts.length > 0 && (
        <div className="zee-feed-post-grid" style={{ display: 'grid' }}>
          {posts.map(postTile)}
        </div>
      )}

      {playingVideoId && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="YouTube video player"
          onClick={() => setPlayingVideoId(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            backdropFilter: 'blur(6px)',
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', maxWidth: '900px', position: 'relative' }}
          >
            <button
              type="button"
              onClick={() => setPlayingVideoId(null)}
              aria-label="Close video"
              style={{
                position: 'absolute',
                top: '-44px',
                right: 0,
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '999px',
                color: 'white',
                padding: '8px',
                cursor: 'pointer',
                display: 'flex',
              }}
            >
              <X size={20} />
            </button>
            <div style={{ position: 'relative', paddingTop: '56.25%', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
              <iframe
                src={`https://www.youtube.com/embed/${playingVideoId}?autoplay=1`}
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
              />
            </div>
          </div>
        </div>
      )}

      <style>{`
        .zee-feed-section {
          padding: 64px 16px;
        }
        .zee-feed-cta-btn {
          padding: 13px 22px;
          font-size: 14px;
        }
        .zee-feed-video-grid {
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 16px;
        }
        .zee-feed-post-grid {
          grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
          gap: 12px;
        }
        @media (max-width: 480px) {
          .zee-feed-section {
            padding: 40px 12px;
          }
          .zee-feed-cta-btn {
            padding: 13px 18px;
            font-size: 13px;
          }
          .zee-feed-video-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }
          .zee-feed-post-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 8px;
          }
        }
      `}</style>
    </section>
  );
};

export default SocialMediaFeed;
